import { authApi } from "@api";
import { Paths } from "@constants/paths";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { AuthToken, MemberSettingFundingMode } from "kz-ui-sdk";
import { useCallback } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

interface UsePostLoginCheckProps {
  onLoginSuccess?: (token: AuthToken) => void;
  onPostLoginCheckPassed?: () => void;
  onPostLoginCheckFailed?: () => void;
  redirectHome?: boolean;
}

/**
 * Post login check hook
 * After login, user need to submit some information to continue the onboarding process
 * This hook is used to check if the user has submitted the information
 * Should call this hook after login success
 *
 * @param onPostLoginCheckPassed - Callback function to be called when post login check passed
 * @param onLoginSuccess - Callback function to be called when login success
 * @param onPostLoginCheckFailed - Callback function to be called when post login check failed
 * @param redirectHome - Whether to redirect to homepage
 * @returns post login check function
 */
const usePostLoginCheck = ({
  onPostLoginCheckPassed,
  onLoginSuccess,
  onPostLoginCheckFailed,
  redirectHome = true,
}: UsePostLoginCheckProps) => {
  const { fundingMode } = useMemberSettings();
  const [fetchProfile] = authApi.useLazyGetProfileWithTokenQuery();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const postLoginCheck = useCallback(
    async (token?: AuthToken) => {
      if (!token) return;

      onLoginSuccess?.(token);

      // redirect to homepage
      if (redirectHome && !matchPath(Paths.PRIVATE.HOME, pathname)) {
        navigate(Paths.PRIVATE.HOME, { replace: true });
      }

      // if no callback check, do not check post login
      if (!onPostLoginCheckPassed || !onPostLoginCheckFailed) {
        return;
      }

      // query profile
      try {
        const profile = await fetchProfile(token.access_token);

        switch (fundingMode) {
          case MemberSettingFundingMode.BankOnly:
            // check bank account
            if (!!profile.data?.self?.member?.fundingProfiles?.length) {
              // user already had bank account, do log in
              onPostLoginCheckPassed?.();
            } else {
              onPostLoginCheckFailed?.();
            }
            break;
          case MemberSettingFundingMode.EWalletOnly:
            // check legal name
            if (!!profile.data?.self?.member?.fullname?.trim()) {
              // user already had legal name, do log in
              onPostLoginCheckPassed?.();
            } else {
              onPostLoginCheckFailed?.();
            }
            break;
        }
      } catch (e) {
        onPostLoginCheckPassed?.();
      }
    },
    [
      fetchProfile,
      onPostLoginCheckPassed,
      fundingMode,
      onPostLoginCheckFailed,
      pathname,
      navigate,
      onLoginSuccess,
      redirectHome,
    ],
  );

  return postLoginCheck;
};

export default usePostLoginCheck;
